import { useMemo, useState } from 'react';
import { BookIcon, XIcon } from 'lucide-react';
import { Button, Checkbox, Popover, PopoverContent, PopoverTrigger } from '@monksflow/monks-ui';
import clsx from 'clsx';
import { useKnowledgeBasesList } from '@/shared/hooks';
import { SearchInput } from '@/shared/ui/SearchInput';
import type { KnowledgeBaseRef } from '../../model/brief-generation.types';
import { useTranslation } from 'react-i18next';

interface KnowledgeBasePickerProps {
  selected: KnowledgeBaseRef[];
  onToggle: (kb: KnowledgeBaseRef) => void;
  onClear: () => void;
}

interface KBItem {
  id: string;
  name: string;
}

export const KnowledgeBasePicker = ({ selected, onToggle, onClear }: KnowledgeBasePickerProps) => {
  const { t } = useTranslation('briefs');

  const [search, setSearch] = useState('');
  const { data: knowledgeBasesData } = useKnowledgeBasesList();

  const selectableKBs = useMemo<KBItem[]>(() => {
    if (!knowledgeBasesData) return [];
    return knowledgeBasesData
      .reduce<KBItem[]>((acc, kb) => {
        if (kb.id && kb.name && !kb.isInternal) acc.push({ id: kb.id, name: kb.name });
        return acc;
      }, [])
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [knowledgeBasesData]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return selectableKBs;
    return selectableKBs.filter((kb) => kb.name.toLowerCase().includes(q));
  }, [selectableKBs, search]);

  const selectedIds = useMemo(() => new Set(selected.map((kb) => kb.id)), [selected]);
  const count = selected.length;

  const clear = (event: { preventDefault: () => void; stopPropagation: () => void }) => {
    event.preventDefault();
    event.stopPropagation();
    onClear();
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          aria-label={
            count > 0
              ? t('briefGeneration.knowledgeBasePicker.knowledgeBasesSelected', {
                  selectedCount: count,
                })
              : t('briefGeneration.knowledgeBasePicker.knowledgeBases2')
          }
          data-testid="kb-picker-trigger"
          className={clsx('group', count > 0 && 'bg-secondary hover:bg-secondary')}
        >
          <BookIcon className="size-4" />
          {count > 0 && (
            <>
              <span className="group-hover:hidden group-focus-within:hidden">{count}</span>
              {/* On hover/focus the count swaps to an X that clears the
                  selection without opening the popover (stop propagation). */}
              <span
                role="button"
                tabIndex={0}
                aria-label={t('briefGeneration.knowledgeBasePicker.clearSelectedKnowledgeBases')}
                data-testid="kb-picker-clear-hover"
                className="hidden group-hover:inline-flex group-focus-within:inline-flex"
                onPointerDown={(event) => event.stopPropagation()}
                onClick={clear}
                onKeyDown={(event) => (event.key === 'Enter' || event.key === ' ') && clear(event)}
              >
                <XIcon className="size-4" />
              </span>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="pointer-events-auto w-80 p-0"
        align="start"
        side="bottom"
        data-testid="kb-picker-popover"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-3 pb-2 pt-3">
          <span className="text-sm font-medium text-muted-foreground">
            {t('briefGeneration.knowledgeBasePicker.knowledgeBases', {
              baseCount: selectableKBs.length,
            })}
          </span>
          {count > 0 && (
            <Button variant="outline" size="sm" onClick={onClear} data-testid="kb-picker-clear">
              {t('briefGeneration.knowledgeBasePicker.clear')}
            </Button>
          )}
        </div>

        {/* Search */}
        <div className="px-2 pb-1">
          <SearchInput
            placeholder={t('briefGeneration.knowledgeBasePicker.search')}
            aria-label={t('briefGeneration.knowledgeBasePicker.searchLabel')}
            value={search}
            onChange={setSearch}
          />
        </div>

        {/* List */}
        <ul className="max-h-70 overflow-y-auto px-2 pb-2" data-testid="kb-picker-list">
          {filtered.map((kb) => {
            const isChecked = selectedIds.has(kb.id);
            return (
              <li
                key={kb.id}
                className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent"
              >
                <label
                  htmlFor={`kb-picker-option-${kb.id}`}
                  className="flex-1 cursor-pointer truncate"
                >
                  {kb.name}
                </label>
                <Checkbox
                  id={`kb-picker-option-${kb.id}`}
                  checked={isChecked}
                  onCheckedChange={() => onToggle({ id: kb.id, name: kb.name })}
                />
              </li>
            );
          })}
          {filtered.length === 0 && (
            <li className="px-3 py-2 text-sm text-muted-foreground">
              {t('briefGeneration.knowledgeBasePicker.noKnowledgeBasesFound')}
            </li>
          )}
        </ul>
      </PopoverContent>
    </Popover>
  );
};
